import type { AddressFormData } from "@/store/auth/forms/useAddressForm";
import { useAddressForm } from "@/store/auth/forms/useAddressForm";
import { useAuthState } from "@/store/auth";
import { useRoute } from "@/components/shared/router";
import { useCreateAddressMutation } from "@/gqlhooks/profile/useCreateAddressMutation";
import {
  createUnitNumber,
  getErrorMessage,
  sanitizeOptionalText,
} from "@/lib/helpers/string";
import { showToast } from "@/components/ui/toast/show-toast";
import { FormProvider } from "react-hook-form";
import { CreateAddressScreen } from "./create-address-screen";
import { useApolloClient } from "@apollo/client";
import type { CreateAddressProps } from "@/types/profile";

export { type CreateAddressProps };

export const CreateAddress = () => {
  const { pull } = useRoute();
  const {
    data: { userInfo },
  } = useAuthState();
  const client = useApolloClient();
  const [createAddressMutation, { loading: createAddressLoading }] =
    useCreateAddressMutation();

  const defaultValues: AddressFormData = {
    label: "",
    postalCode: "",
    fullAddress: "",
    unitFloor: "",
    apartmentNumber: "",
    primary: false,
  };

  const form = useAddressForm(defaultValues);
  const { handleSubmit } = form;

  const onSubmit = handleSubmit(async (data: AddressFormData) => {
    try {
      const result = await createAddressMutation({
        variables: {
          input: {
            clientId: userInfo.id,
            label: sanitizeOptionalText(data.label),
            fullAddress: data.fullAddress,
            postalCode: data.postalCode,
            unitNumber: createUnitNumber(data.unitFloor, data.apartmentNumber),
            primary: data.primary,
          },
        },
      });

      if (result.data?.createAddress?.address) {
        // Evict and refetch the addresses cache
        client.cache.evict({ fieldName: "clientAddresses" });
        client.cache.gc();
        pull();
        showToast({
          title: "Success create address",
          type: "success",
        });
      }
    } catch (error) {
      const errMsg = getErrorMessage(error, "Error create address");
      showToast({
        title: errMsg,
        type: "error",
      });
    }
  });

  return (
    <FormProvider {...form}>
      <CreateAddressScreen
        onSubmit={onSubmit}
        createAddressLoading={createAddressLoading}
      />
    </FormProvider>
  );
};
